import React, { useState, useEffect } from 'react';
import { usePushNotifications } from '../hooks/usePushNotifications';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from 'sonner';
import { Bell, BellOff, BellRing, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

const NOTIFICATION_TYPES = [
  { key: 'new_event', label: 'Nya aktiviteter', description: 'När nya träningar eller matcher skapas' },
  { key: 'event_reminder', label: 'Påminnelser', description: 'Påminnelse innan aktiviteter börjar' },
  { key: 'rsvp_reminder', label: 'Kallelser', description: 'Påminnelse att svara på närvaro' },
  { key: 'new_chat_message', label: 'Chattmeddelanden', description: 'Nya meddelanden i lagchatten' },
  { key: 'evaluation_request', label: 'Utvärderingar', description: 'När du får en ny utvärdering att svara på' },
  { key: 'new_praise', label: 'Beröm', description: 'När du får nytt beröm' },
  { key: 'match_result', label: 'Matchresultat', description: 'När matchresultat registreras' }
];

export default function NotificationSettings() {
  const {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTestNotification
  } = usePushNotifications();
  
  const [preferences, setPreferences] = useState({});
  const [loadingPrefs, setLoadingPrefs] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const res = await api.get('/push/preferences');
      setPreferences(res.data);
    } catch (err) {
      console.error('Failed to load preferences:', err);
    } finally {
      setLoadingPrefs(false);
    }
  };

  const handleSubscribe = async () => {
    const success = await subscribe();
    if (success) {
      toast.success('Push-notifikationer aktiverade!');
    } else if (permission === 'denied') {
      toast.error('Du har blockerat notifikationer. Tillåt dem i webbläsarens inställningar.');
    }
  };

  const handleUnsubscribe = async () => {
    const success = await unsubscribe();
    if (success) {
      toast.success('Push-notifikationer avaktiverade');
    }
  };

  const handlePreferenceChange = async (key, value) => {
    try {
      await api.put('/push/preferences', { [key]: value });
      setPreferences({ ...preferences, [key]: value });
      toast.success('Inställning sparad');
    } catch (err) {
      toast.error('Kunde inte spara inställning');
    }
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    try {
      const result = await sendTestNotification();
      if (result.sent > 0) {
        toast.success('Testnotifikation skickad!');
      } else if (result.skipped) {
        toast.info('Ingen prenumeration aktiv');
      } else {
        toast.error('Kunde inte skicka notifikation');
      }
    } catch (err) {
      toast.error('Fel vid skickande av testnotifikation');
    } finally {
      setSendingTest(false);
    }
  };

  if (!isSupported) {
    return (
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <BellOff className="w-5 h-5" />
            Push-notifikationer
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Din webbläsare stöder inte push-notifikationer. Prova med en nyare webbläsare som Chrome, Firefox eller Safari.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border" data-testid="notification-settings">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Bell className="w-5 h-5 text-primary" />
          Push-notifikationer
        </CardTitle>
        <CardDescription>
          Få notifikationer om viktiga händelser i appen
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Subscription status */}
        <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
          <div className="flex items-center gap-3">
            {isSubscribed ? (
              <div className="p-2 rounded-full bg-primary/20">
                <BellRing className="w-5 h-5 text-primary" />
              </div>
            ) : (
              <div className="p-2 rounded-full bg-muted">
                <BellOff className="w-5 h-5 text-muted-foreground" />
              </div>
            )}
            <div>
              <p className="font-medium text-[rgb(var(--tn-text-ffffff))]">
                {isSubscribed ? 'Notifikationer aktiva' : 'Notifikationer avstängda'}
              </p>
              <p className="text-sm text-muted-foreground">
                {isSubscribed 
                  ? 'Du får push-notifikationer'
                  : 'Aktivera för att få viktiga uppdateringar'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {permission === 'denied' && (
              <Badge variant="destructive">Blockerad</Badge>
            )}
            <Button
              variant={isSubscribed ? 'outline' : 'default'}
              onClick={isSubscribed ? handleUnsubscribe : handleSubscribe}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isSubscribed ? (
                'Stäng av'
              ) : (
                'Aktivera'
              )}
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {permission === 'denied' && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Du har blockerat notifikationer för denna webbplats. För att aktivera dem:
              <ol className="list-decimal ml-4 mt-2 space-y-1 text-sm">
                <li>Klicka på låsikonen i adressfältet</li>
                <li>Hitta "Meddelanden" eller "Notifications"</li>
                <li>Ändra till "Tillåt"</li>
                <li>Ladda om sidan</li>
              </ol>
            </AlertDescription>
          </Alert>
        )}

        {/* Notification preferences */}
        {isSubscribed && (
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">
              Välj vilka notifikationer du vill få
            </h3>
            
            {NOTIFICATION_TYPES.map((type) => (
              <div 
                key={type.key}
                className="flex items-center justify-between py-3 border-b border-border last:border-0"
              >
                <div>
                  <Label className="text-[rgb(var(--tn-text-ffffff))]">{type.label}</Label>
                  <p className="text-sm text-muted-foreground">{type.description}</p>
                </div>
                <Switch
                  checked={preferences[type.key] !== false}
                  onCheckedChange={(v) => handlePreferenceChange(type.key, v)}
                  disabled={loadingPrefs}
                />
              </div>
            ))}
          </div>
        )}

        {/* Test notification */}
        {isSubscribed && (
          <div className="pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={handleSendTest}
              disabled={sendingTest}
              className="w-full"
              data-testid="send-test-notification"
            >
              {sendingTest ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <BellRing className="w-4 h-4 mr-2" />
              )}
              Skicka testnotifikation
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
